import type {
  PaymentNextAction,
  PaymentPhase,
  PaymentStatusResult,
  RawSignResponse,
  RawX402AuthorizeResponse,
} from './types.js'
import {
  AgentPaymentNextAction,
  AgentPaymentPhase,
  HavenApiError,
  HavenPaymentStateError,
} from './types.js'

const PAYMENT_STATE_STATUS_CODES: Record<string, number> = {
  pending: 202,
  pending_approval: 202,
  approved: 202,
  proposed: 202,
  executed: 200,
  pending_signature: 409,
  submitted: 409,
  expired: 410,
  failed: 502,
  rejected: 409,
}

export function paymentStateStatusCode(status: string, fallback = 502): number {
  return PAYMENT_STATE_STATUS_CODES[status] ?? fallback
}

// The three retired-queue statuses keep their descriptive phase labels on
// purpose, and #2262 deliberately did not make `approved` the exception.
// #2101 and #2145 both converted `nextActionForStatus` only and left every
// arm of this function alone: `phase` is a descriptive label, `nextAction` is
// the field the agent contract says to follow FIRST, and all three phases
// these arms return — `user_approval_required`, `user_execution_required`,
// `waiting_for_additional_approvals` — already carry the "Retired wire value:
// no live rail produces it. Stop and tell the user." text in
// `AgentPaymentPhaseDescriptions` (`types.ts`). Re-pointing one of the three
// at a different phase would make the status→phase correspondence lie in a
// new way and would be a third handling of one fact.
function phaseForStatus(status: string): PaymentPhase | null {
  if (status === 'pending_signature') return AgentPaymentPhase.AgentSignatureRequired
  if (status === 'submitted') return AgentPaymentPhase.PaymentSubmitted
  if (status === 'confirmed') return AgentPaymentPhase.PaymentConfirmed
  if (status === 'pending' || status === 'pending_approval') return AgentPaymentPhase.UserApprovalRequired
  if (status === 'approved') return AgentPaymentPhase.UserExecutionRequired
  if (status === 'proposed') return AgentPaymentPhase.WaitingForAdditionalApprovals
  if (status === 'executed') return AgentPaymentPhase.FundingSent
  if (status === 'rejected') return AgentPaymentPhase.Rejected
  if (status === 'expired') return AgentPaymentPhase.Expired
  if (status === 'failed') return AgentPaymentPhase.Failed
  return null
}

function nextActionForStatus(status: string): PaymentNextAction | null {
  if (status === 'pending_signature') return AgentPaymentNextAction.SignAndSubmitPayment
  if (status === 'submitted') return AgentPaymentNextAction.CheckStatusLater
  if (status === 'confirmed') return AgentPaymentNextAction.None
  // #2101: STOP, not wait. No live rail mints these payment statuses (410 on
  // the legacy rail, #1986; 403/502 at prepare on the delegation rail;
  // `approval_requests` dropped by #2055), so nothing will ever transition a
  // row out of them. `nextAction` is the field the agent contract says to
  // follow FIRST — emitting `wait_for_user_approval` here would contradict
  // the message beside it and send a compliant agent into a poll loop that
  // cannot terminate. The fail-closed branch is retained; its verdict is stop.
  if (status === 'pending' || status === 'pending_approval') return AgentPaymentNextAction.StopAndTellUser
  // #2262: `approved` closes the #2101 sweep — it was the one branch left
  // emitting a wait, sitting literally between two converted siblings. Same
  // proof as theirs, re-verified rather than inherited: `approved` was an
  // `approval_requests` status (table dropped by #2055), and no write in the
  // repository layer sets `payment_intents.status` to it — every INSERT/UPDATE
  // in `infra/repositories/payment-intents.ts`, `x402-authorizations.ts` and
  // `agent-rekeys.ts` sets `pending_signature`, `submitted`, `confirmed`,
  // `expired` or `failed`. So nothing can mint it, and the wait it returned
  // pointed at a user-execution step on the Safe rail, which has answered 410
  // to everything since #1986. An agent that followed it would wait forever.
  // Three places already said so — `AgentPaymentNextActionDescriptions` and
  // `AgentPaymentPhaseDescriptions` in `types.ts`, and README's next-action
  // table — so the tree contradicted itself here. Fail-closed branch retained;
  // its verdict is stop.
  if (status === 'approved') return AgentPaymentNextAction.StopAndTellUser
  if (status === 'proposed') return AgentPaymentNextAction.StopAndTellUser
  // #2145: `executed` joins its #2101 siblings, fail-closed. It was an
  // `approval_requests` status (table dropped, #2055) and cannot be
  // constructed as a payment_intents.status, so the retry instruction it
  // carried pointed at a state nothing can mint. The reachable producer of
  // retry_original_x402_request is the backend's status projection
  // (funded-but-undelivered on the eip3009 bridge), which arrives via the
  // response's own `next_action` — never through this status fallback.
  if (status === 'executed') return AgentPaymentNextAction.StopAndTellUser
  if (status === 'rejected') return AgentPaymentNextAction.StopAndTellUser
  if (status === 'expired') return AgentPaymentNextAction.RequestAgainIfUserStillWantsIt
  if (status === 'failed') return AgentPaymentNextAction.StopAndTellUser
  return null
}

function messageForState(
  label: string,
  status: string,
  paymentId: string,
  nextAction: PaymentNextAction,
): string {
  if (status === 'pending' || status === 'pending_approval') {
    // No live Haven rail mints these statuses for a payment: an out-of-policy
    // payment is refused during prepare (403/502 from POST /payments) with
    // nothing written, and the queue table the old text pointed at is gone.
    // The branch is kept fail-closed for a stored row from before the
    // retirement, but the message must not promise an approval that will
    // never arrive — the agent is told to stop, not to poll.
    return `${label} is not payable: it is outside the agent's on-chain budget and no approval is pending (payment_id: ${paymentId}). Ask the user to grant or raise the budget in Haven.`
  }
  if (status === 'approved') {
    // #2262: no live rail mints this status (it belonged to the dropped
    // approval queue), so the message must not promise a payment the user is
    // about to complete. Before this branch existed the generic template below
    // rendered "<label> is approved; next_action=wait_for_user_to_complete_payment",
    // which is the instruction, not the documentation, an agent acts on.
    return `This payment carries a retired status ("approved") that no live Haven rail produces (payment_id: ${paymentId}). Nothing is waiting to be completed — tell the user to review this payment in Haven.`
  }
  if (status === 'executed') {
    // #2145: no live rail mints this status (it belonged to the dropped
    // approval queue), so the message must not promise a retry.
    return `This payment carries a retired status ("executed") that no live Haven rail produces (payment_id: ${paymentId}). Do not retry it — tell the user to review this payment in Haven.`
  }
  if (status === 'rejected') {
    return `The user rejected this payment request (payment_id: ${paymentId}).`
  }
  if (status === 'expired') {
    return `This payment request expired (payment_id: ${paymentId}).`
  }
  return `${label} is ${status}; next_action=${nextAction} (payment_id: ${paymentId}).`
}

export function paymentStateFromRaw(
  label: string,
  raw: RawX402AuthorizeResponse | RawSignResponse,
): PaymentStatusResult | null {
  if (!raw.payment_id || !raw.status) return null

  const phase = (raw.phase as PaymentPhase | undefined) ?? phaseForStatus(raw.status)
  const nextAction =
    (raw.next_action as PaymentNextAction | undefined) ?? nextActionForStatus(raw.status)
  if (!phase || !nextAction) return null

  const amount = raw.amount ?? raw.requested ?? ''
  const token = raw.token ?? ''
  const message =
    raw.message ??
    raw.error ??
    messageForState(label, raw.status, raw.payment_id, nextAction)

  return {
    paymentId: raw.payment_id,
    kind: raw.kind === 'payment_intent' ? 'payment_intent' : 'approval_request',
    rail: raw.rail ?? 'direct',
    status: raw.status === 'pending' ? 'pending_approval' : raw.status,
    phase,
    nextAction,
    amount,
    token,
    resourceUrl: raw.resource_url ?? null,
    merchantAddress: raw.merchant_address ?? raw.merchant_to ?? null,
    txHash: raw.tx_hash ?? null,
    expiresAt: raw.expires_at ?? '',
    chainId: raw.chain_id ?? 0,
    message,
    amountAtomic: raw.amount_atomic ?? raw.x402?.amount_atomic ?? raw.mpp?.amount_atomic ?? null,
    asset: raw.asset ?? raw.x402?.asset ?? raw.mpp?.asset ?? null,
    network: raw.network ?? raw.x402?.network ?? raw.mpp?.network ?? null,
    description: raw.description ?? raw.x402?.description ?? raw.mpp?.description ?? null,
    idempotencyKey: raw.idempotency_key ?? raw.x402?.idempotency_key ?? raw.mpp?.idempotency_key ?? null,
    x402: raw.x402
      ? {
          amountAtomic: raw.x402.amount_atomic ?? raw.amount_atomic ?? null,
          asset: raw.x402.asset ?? raw.asset ?? null,
          network: raw.x402.network ?? raw.network ?? null,
          resourceUrl: raw.x402.resource_url ?? raw.resource_url ?? null,
          merchantAddress: raw.x402.merchant_address ?? raw.merchant_address ?? raw.merchant_to ?? null,
          description: raw.x402.description ?? raw.description ?? null,
          idempotencyKey: raw.x402.idempotency_key ?? raw.idempotency_key ?? null,
        }
      : undefined,
    mpp: raw.mpp
      ? {
          amountAtomic: raw.mpp.amount_atomic ?? raw.amount_atomic ?? null,
          asset: raw.mpp.asset ?? raw.asset ?? null,
          network: raw.mpp.network ?? raw.network ?? null,
          resourceUrl: raw.mpp.resource_url ?? raw.resource_url ?? null,
          merchantAddress: raw.mpp.merchant_address ?? raw.merchant_address ?? raw.merchant_to ?? null,
          description: raw.mpp.description ?? raw.description ?? null,
          idempotencyKey: raw.mpp.idempotency_key ?? raw.idempotency_key ?? null,
          challengeId: raw.mpp.challenge_id ?? raw.challenge_id ?? null,
        }
      : undefined,
  }
}

export function throwPaymentStateError(
  label: string,
  raw: RawX402AuthorizeResponse | RawSignResponse,
): never {
  const statusCode = paymentStateStatusCode(raw.status)
  const state = paymentStateFromRaw(label, raw)

  if (state) {
    throw new HavenPaymentStateError(state.message, statusCode, state, raw)
  }

  if (raw.status === 'pending_approval') {
    throw new HavenApiError(
      `${label} exceeds the agent's on-chain budget and was declined; no approval is pending and none will arrive (payment_id: ${raw.payment_id}).`,
      statusCode,
      raw,
    )
  }

  if (raw.status === 'expired') {
    throw new HavenApiError(
      `${label} expired before it could be completed (payment_id: ${raw.payment_id}).`,
      statusCode,
      raw,
    )
  }

  const paymentId = raw.payment_id ? ` (payment_id: ${raw.payment_id})` : ''
  const message = raw.error ?? `${label} ${raw.status}${paymentId}`
  throw new HavenApiError(message, statusCode, raw)
}
